/**
 * SupplierCombobox — Miroir de ClientCombobox côté Achats.
 *
 * Sélecteur fournisseur avec autocomplete (nom, SIRET, SIREN, VAT) + option
 * "+ Créer un nouveau fournisseur" qui ouvre SupplierEditorSheet inline.
 * Sur sélection, remonte le blob supplier complet (auto-remplit IBAN/SIRET/
 * SIREN dans le formulaire parent) et évite la création de doublons manuels.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Search, Plus, Building2, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import SupplierEditorSheet from "@/components/SupplierEditorSheet";

export default function SupplierCombobox({
  value,          // { id, name, siret, iban, ... } | null
  onSelect,       // (supplier) => void
  onClear,        // () => void
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/suppliers");
        setAll(data.suppliers || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [open]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return all.slice(0, 30);
    return all.filter((c) =>
      (c.name || "").toLowerCase().includes(s) ||
      (c.siret || "").includes(s) ||
      (c.siren || "").includes(s) ||
      (c.vat_number || "").toLowerCase().includes(s),
    ).slice(0, 30);
  }, [q, all]);

  const handleSelect = async (c) => {
    // Recharge le détail complet (IBAN déchiffré) pour auto-remplissage
    try {
      const { data } = await api.get(`/suppliers/${c.id}`);
      onSelect?.(data.supplier);
    } catch { onSelect?.(c); }
    setOpen(false); setQ("");
  };

  const handleCreated = (c) => {
    setAll((a) => [c, ...a]);
    onSelect?.(c);
    setEditorOpen(false);
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
          <PopoverTrigger asChild>
            <button type="button" disabled={disabled}
                     data-testid="supplier-combobox-trigger"
                     className={cn(
                       "flex-1 flex items-center gap-2 rounded-md border border-slate-200 bg-white",
                       "px-3 py-2 text-sm text-slate-800 hover:border-slate-300 transition-colors",
                       "disabled:bg-slate-50 disabled:cursor-not-allowed",
                     )}>
              <Building2 className="h-4 w-4 text-slate-400 shrink-0" />
              {value?.name ? (
                <div className="flex-1 flex items-center gap-2 min-w-0">
                  <span className="truncate font-medium">{value.name}</span>
                  {value.siret && (
                    <span className="font-mono text-[10px] text-slate-400 shrink-0">
                      {value.siret}
                    </span>
                  )}
                </div>
              ) : (
                <span className="flex-1 text-slate-400">Sélectionner un fournisseur…</span>
              )}
              <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-[420px]" align="start"
                          data-testid="supplier-combobox-popover">
            <div className="border-b border-slate-200 p-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)}
                        placeholder="Rechercher (nom, SIRET, SIREN, TVA…)"
                        autoFocus className="pl-8 h-9 text-sm"
                        data-testid="supplier-combobox-search" />
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-xs text-slate-400">Chargement…</div>
              ) : filtered.length === 0 ? (
                <div className="p-6 text-center text-xs text-slate-500">
                  Aucun fournisseur ne matche.
                </div>
              ) : filtered.map((c) => (
                <button key={c.id} onClick={() => handleSelect(c)} type="button"
                         data-testid={`supplier-combobox-option-${c.id}`}
                         className="w-full flex items-start gap-2 px-3 py-2 hover:bg-slate-50 text-left border-b border-slate-100">
                  <Building2 className="h-3.5 w-3.5 text-slate-400 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{c.name}</div>
                    <div className="flex items-center gap-2 text-[10px] font-mono text-slate-500">
                      {c.siret && <span>SIRET {c.siret}</span>}
                      {c.city && <span>· {c.city}</span>}
                      {c.invoice_count > 0 && <span>· {c.invoice_count} facture(s)</span>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div className="border-t border-slate-200 p-2">
              <Button variant="ghost" size="sm" onClick={() => setEditorOpen(true)}
                       className="w-full justify-start text-blue-700 hover:bg-blue-50"
                       data-testid="supplier-combobox-create-btn">
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Créer un nouveau fournisseur
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        {value && !disabled && (
          <Button variant="ghost" size="icon" onClick={onClear}
                   data-testid="supplier-combobox-clear-btn"
                   className="h-9 w-9 text-slate-400 hover:text-red-600">
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      <SupplierEditorSheet supplierId={null} open={editorOpen}
                            onOpenChange={setEditorOpen}
                            onSaved={handleCreated} />
    </>
  );
}
